import React, { useEffect, useMemo, useState } from 'react';
import { AuthPermissions } from 'skipify-types';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  ConfigResponse,
  FlexPayConfigValue,
  FlexPayVertical,
} from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { ConfigSwitchSection, ParentAwareSaveBar } from '../../common';
import { inheritableLabelsFromConfigNames } from '../../util/parentSettingSave';
import { useConfigUpsert } from '../../hooks';
import { useMerchant } from '../../contexts/MerchantContext';
import { useDeleteConfigMutation, useGetConfigsQuery } from '../../store/modules/merchant/api';
import { toast } from '../../util/toast';
type FlexPayDraft = Partial<{
  enabled: boolean;
  upCode: string;
  useSkipifyUpCode: boolean;
  vertical: FlexPayVertical;
}>;

const VERTICAL_OPTIONS: { value: FlexPayVertical; label: string }[] = [
  { value: 'air', label: 'Air' },
  { value: 'cruise', label: 'Cruise' },
  { value: 'event_tickets', label: 'Event tickets' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'passes_subscription', label: 'Passes / subscription' },
  { value: 'rentals', label: 'Rentals' },
  { value: 'retail', label: 'Retail' },
];

const DEFAULT_VERTICAL: FlexPayVertical = VERTICAL_OPTIONS[0].value;

function isFlexPayVertical(value: unknown): value is FlexPayVertical {
  return VERTICAL_OPTIONS.some((option) => option.value === value);
}

function getSkipifyToggleDescription(useSkipifyUpCode: boolean): string {
  if (useSkipifyUpCode) {
    return "Skipify provides the UP-code at checkout. Use this if you are on Skipify's Flex Pay program and do not have your own Flex Pay merchant ID.";
  }
  return "Enable to use Skipify's UP-code instead of entering your own.";
}

function getFlexPayValueFromConfig(
  config: ConfigResponse<FlexPayConfigValue> | undefined,
): Required<Pick<FlexPayDraft, 'upCode' | 'useSkipifyUpCode' | 'vertical'>> {
  const v = config?.value;
  if (!v || typeof v !== 'object') {
    return { upCode: '', useSkipifyUpCode: false, vertical: DEFAULT_VERTICAL };
  }

  const value = v as FlexPayConfigValue;
  const vertical = isFlexPayVertical(value.vertical) ? value.vertical : DEFAULT_VERTICAL;
  const useSkipifyUpCode = value.useSkipifyUpCode === true;

  return {
    upCode: typeof value.upCode === 'string' ? value.upCode : '',
    useSkipifyUpCode,
    vertical,
  };
}

function buildFlexPayConfigValue(
  useSkipifyUpCode: boolean,
  upCode: string,
  vertical: FlexPayVertical,
): FlexPayConfigValue {
  if (useSkipifyUpCode) {
    return { useSkipifyUpCode: true, vertical };
  }

  return { useSkipifyUpCode: false, upCode: upCode.trim(), vertical };
}

export const FlexPay = () => {
  const {
    merchantId,
    isLoading: merchantInfoLoading,
    canAccess,
    canUpdateMerchantSettings,
  } = useMerchant();
  const hasModifyAccess = canUpdateMerchantSettings();
  const readOnly = !hasModifyAccess;

  const { data: configs, isLoading: isLoadingConfigs } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const flexPayConfig = configs?.find((c) => c.name === 'FLEX_PAY') as
    ConfigResponse<FlexPayConfigValue> | undefined;

  const { onConfigUpsert, isLoading: isUpserting } = useConfigUpsert(merchantId);
  const [deleteConfig, { isLoading: isDeleting }] = useDeleteConfigMutation();
  const isSaving = isUpserting || isDeleting;

  const fromServer = useMemo(
    () => ({
      enabled: flexPayConfig?.enabled ?? false,
      ...getFlexPayValueFromConfig(flexPayConfig),
    }),
    [flexPayConfig],
  );

  const [draft, setDraft] = useState<FlexPayDraft>({});
  const [upCodeError, setUpCodeError] = useState<string | null>(null);
  const [verticalError, setVerticalError] = useState<string | null>(null);

  useEffect(() => {
    setDraft({});
    setUpCodeError(null);
    setVerticalError(null);
  }, [
    fromServer.enabled,
    fromServer.upCode,
    fromServer.useSkipifyUpCode,
    fromServer.vertical,
    flexPayConfig?.configId,
  ]);

  const enabled = draft.enabled ?? fromServer.enabled;
  const upCode = draft.upCode ?? fromServer.upCode;
  const vertical = draft.vertical ?? fromServer.vertical;
  const useSkipifyUpCode = draft.useSkipifyUpCode ?? fromServer.useSkipifyUpCode;
  const showUpCodeField = !useSkipifyUpCode;

  const isDirty =
    enabled !== fromServer.enabled ||
    useSkipifyUpCode !== fromServer.useSkipifyUpCode ||
    vertical !== fromServer.vertical ||
    (!useSkipifyUpCode && upCode.trim() !== fromServer.upCode.trim());

  const handleSave = async () => {
    setUpCodeError(null);
    setVerticalError(null);

    if (enabled && !vertical) {
      setVerticalError('Flex Pay vertical is required');
      return;
    }

    if (enabled && !useSkipifyUpCode && !upCode.trim()) {
      setUpCodeError('UP-code is required');
      return;
    }

    const cfgMerchantId = flexPayConfig?.merchantId;
    const isInherited =
      Boolean(cfgMerchantId) && (cfgMerchantId !== merchantId || !!flexPayConfig?.isInherited);

    const shouldDeleteConfig =
      !enabled && !useSkipifyUpCode && !upCode.trim() && !!flexPayConfig?.configId && !isInherited;

    if (shouldDeleteConfig) {
      if (!canAccess(AuthPermissions.DELETE_MERCHANTS)) {
        toast.error(
          'You do not have permission to remove this configuration. Please contact your administrator.',
        );
        return;
      }
      try {
        await deleteConfig({ merchantId, configId: flexPayConfig.configId }).unwrap();
        toast.success('Changes saved successfully');
      } catch {
        toast.error(
          'An error occurred while saving these changes. Please try again later or contact Skipify Support',
        );
      }
      return;
    }

    if (!enabled && !useSkipifyUpCode && !upCode.trim() && flexPayConfig?.configId && isInherited) {
      toast.error(
        'This Flex Pay configuration is inherited and cannot be removed from this account.',
      );
      return;
    }

    try {
      await onConfigUpsert({
        name: 'FLEX_PAY',
        value: buildFlexPayConfigValue(useSkipifyUpCode, upCode, vertical),
        enabled,
        configId: flexPayConfig?.configId,
        merchantId: flexPayConfig?.merchantId,
      });
      toast.success('Changes saved successfully');
    } catch {
      toast.error(
        'An error occurred while saving these changes. Please try again later or contact Skipify Support',
      );
    }
  };

  const resetValues = () => {
    setDraft({});
    setUpCodeError(null);
    setVerticalError(null);
  };

  return (
    <PageWrapper
      showParentHierarchyBanner
      data-testid="FLEX_PAY"
      title="Flex Pay"
      description="Turn on Flex Pay, choose your vertical, and configure your UP-code."
      isLoading={isLoadingConfigs || merchantInfoLoading}
    >
      <Box sx={{ maxWidth: 600 }} key={configs?.[0]?.merchantId ?? merchantId}>
        <Stack gap={3}>
          <ConfigSwitchSection
            title="Flex Pay integration"
            isChecked={enabled}
            onToggle={() => {
              setUpCodeError(null);
              setVerticalError(null);
              setDraft((prev) => ({
                ...prev,
                enabled: !(prev.enabled ?? fromServer.enabled),
              }));
            }}
            disabled={readOnly}
            switchTestId="FLEX_PAY_ENABLED"
          />
          {enabled && (
            <>
              <FormControl fullWidth required error={!!verticalError}>
                <InputLabel id="flex-pay-vertical-label">Flex Pay vertical</InputLabel>
                <Select
                  labelId="flex-pay-vertical-label"
                  label="Flex Pay vertical"
                  value={vertical}
                  onChange={(e) => {
                    const nextVertical = e.target.value as FlexPayVertical;
                    setVerticalError(null);
                    setUpCodeError(null);
                    setDraft((prev) => ({
                      ...prev,
                      vertical: nextVertical,
                    }));
                  }}
                  disabled={readOnly}
                  inputProps={{ 'data-testid': 'FLEX_PAY_VERTICAL' }}
                >
                  {VERTICAL_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {verticalError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {verticalError}
                  </Typography>
                )}
              </FormControl>
              <ConfigSwitchSection
                title="Use Skipify UP-code"
                description={getSkipifyToggleDescription(useSkipifyUpCode)}
                isChecked={useSkipifyUpCode}
                onToggle={() => {
                  setUpCodeError(null);
                  setVerticalError(null);
                  setDraft((prev) => ({
                    ...prev,
                    useSkipifyUpCode: !(prev.useSkipifyUpCode ?? fromServer.useSkipifyUpCode),
                  }));
                }}
                disabled={readOnly}
                switchTestId="FLEX_PAY_USE_SKIPIFY_UP_CODE"
              />
              {showUpCodeField && (
                <TextField
                  fullWidth
                  required
                  label="UP-code"
                  value={upCode}
                  onChange={(e) => {
                    setUpCodeError(null);
                    setDraft((prev) => ({ ...prev, upCode: e.target.value }));
                  }}
                  placeholder="Enter your Flex Pay UP-code"
                  disabled={readOnly}
                  error={!!upCodeError}
                  helperText={upCodeError ?? 'Enter the UP-code Flex Pay issued to your business.'}
                  inputProps={{ 'data-testid': 'FLEX_PAY_UP_CODE' }}
                />
              )}
            </>
          )}
        </Stack>
        <ParentAwareSaveBar
          disableSave={readOnly || isSaving}
          saveLoading={isSaving}
          show={isDirty && !readOnly}
          onCancel={resetValues}
          inheritableSettingLabels={isDirty ? inheritableLabelsFromConfigNames(['FLEX_PAY']) : []}
          onSave={handleSave}
        />
      </Box>
    </PageWrapper>
  );
};
